import { DeepPartial } from '@reduxjs/toolkit';
import { api } from 'services/api';
import {
  Device,
  isUpdateDeviceServiceDateParam,
  LogRecord,
  type UpdateUserLastServiceParams,
  type UpdateDeviceLastServiceParams
} from 'types';

interface DevicesResponse {
  Username: string;
  devices: Device[];
  next?: string;
}

interface GetDevicesParams {
  userId: string;
  limit?: number;
  next?: string;
  sort?: string;
  filter?: string;
  short?: boolean;
  all?: boolean;
}

interface GetDeviceLogParams {
  from: string;
  to: string;
  event: string;
  deviceId: string;
}

interface LogsResponse {
  Items: LogRecord[];
}

const devicesApi = api
  .enhanceEndpoints({
    addTagTypes: ['Devices', 'Device', 'UserAuditLogs']
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getAllDevices: build.query<DevicesResponse, GetDevicesParams>({
        query: ({ userId, limit = 100, all, ...params }) => ({
          url: all ? `/devices/all` : `/${userId}/devices`,
          method: 'GET',
          params: {
            limit,
            ...params
          }
        }),
        providesTags: (_res, _err, params) => [
          { type: 'Devices', id: params.userId }
        ]
      }),
      logs: build.query<LogsResponse, GetDeviceLogParams>({
        query: ({ deviceId, ...params }) => ({
          url: `/device/${deviceId}/logs`,
          method: 'GET',
          params
        })
      }),
      createDevice: build.mutation<
        Device,
        { userId: string; data: DeepPartial<Device> }
      >({
        query: ({ userId, data }) => ({
          url: `/${userId}/devices`,
          method: 'POST',
          data
        }),
        invalidatesTags: (_res, _err, params) => [
          { type: 'Devices', id: params.userId }
        ]
      }),
      updateServiceDate: build.mutation<
        void,
        UpdateUserLastServiceParams | UpdateDeviceLastServiceParams
      >({
        query: (params) => ({
          url: isUpdateDeviceServiceDateParam(params)
            ? `/devices/${params.deviceId}/lastservice`
            : `/${params.userId}/devices/lastservice`,
          method: 'PUT',
          data: {
            LastService: params.serviceDate,
            Comment: params.comment // Optional comment
          }
        }),
        invalidatesTags: (_res, _err, params) => [
          { type: 'Device', id: params.deviceId },
          'Devices',
          'UserAuditLogs'
        ]
      }),
      deleteDevice: build.mutation<void, string>({
        query: (deviceId) => ({
          url: `/devices/${deviceId}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['Devices']
      })
    })
  });

export const {
  useGetAllDevicesQuery,
  useLogsQuery,
  useUpdateServiceDateMutation,
  useCreateDeviceMutation,
  useDeleteDeviceMutation
} = devicesApi;
